import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { env } from '@/core/config/env';
import { AppIcon } from './AppIcon';

const parseUrl = (value: string): URL | null => {
  try {
    return new URL(resolveCatalogResourceUrl(value), window.location.origin);
  } catch {
    return null;
  }
};

export const normalizeCatalogUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^(?:https?:\/\/|\/\/)/i.test(trimmed)) {
    return trimmed.startsWith('//') ? `${window.location.protocol}${trimmed}` : trimmed;
  }
  if (/^(?:\/|\.\/|\.\.\/|#|\?)/.test(trimmed)) return trimmed;
  if (/^[a-z][a-z\d+.-]*:/i.test(trimmed)) return '';
  return `https://${trimmed}`;
};

/** Uploaded catalog files are served by the API host, while ordinary relative links stay on the public app. */
export const resolveCatalogResourceUrl = (value: string): string => {
  const normalized = normalizeCatalogUrl(value);
  if (!normalized || !normalized.startsWith('/uploads/')) return normalized;
  return env.apiBaseUrl ? `${env.apiBaseUrl.replace(/\/$/, '')}${normalized}` : normalized;
};

export const isPdfUrl = (value: string | null | undefined): boolean =>
  Boolean(value && /\.pdf(?:[?#]|$)/i.test(value));

export const isExternalCatalogUrl = (value: string): boolean => {
  const target = parseUrl(value);
  if (!target || !['http:', 'https:'].includes(target.protocol)) return false;

  const internalOrigins = new Set([window.location.origin]);
  if (env.apiBaseUrl) {
    const apiUrl = parseUrl(env.apiBaseUrl);
    if (apiUrl) internalOrigins.add(apiUrl.origin);
  }

  return !internalOrigins.has(target.origin) && target.hostname !== window.location.hostname;
};

interface CatalogLinkProperties {
  href: string;
  target?: '_blank';
  rel?: string;
}

export const catalogLinkProps = (value: string): CatalogLinkProperties => {
  const href = resolveCatalogResourceUrl(value);
  return isExternalCatalogUrl(href)
    ? { href, target: '_blank', rel: 'noopener noreferrer' }
    : { href };
};

const pdfResourceUrl = (value: string): string => {
  const normalized = resolveCatalogResourceUrl(value);
  const target = parseUrl(normalized);
  if (!target || target.origin === window.location.origin) return normalized;
  return target.hostname === window.location.hostname
    ? `${target.pathname}${target.search}`
    : normalized;
};

interface PdfViewerModalProps {
  title: string;
  url: string;
  onClose: () => void;
}

type ViewerStatus = 'loading' | 'ready' | 'error';

export const PdfViewerModal = ({ title, url, onClose }: PdfViewerModalProps): React.ReactElement => {
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<ViewerStatus>('loading');
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const stopSaveAndPrint = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && ['p', 's'].includes(event.key.toLowerCase())) {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', stopSaveAndPrint);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', stopSaveAndPrint);
    };
  }, [onClose]);

  useEffect(() => {
    let disposed = false;
    let loadingTask: PDFDocumentLoadingTask | undefined;
    let pdfDocument: PDFDocumentProxy | undefined;
    let renderVersion = 0;
    let resizeTimer: number | undefined;
    let lastWidth = 0;
    const observer = new ResizeObserver((entries) => {
      const nextWidth = Math.round(entries[0]?.contentRect.width ?? 0);
      if (!pdfDocument || Math.abs(nextWidth - lastWidth) < 8) return;
      lastWidth = nextWidth;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => void renderPages(pdfDocument!), 180);
    });

    const renderPages = async (pdf: PDFDocumentProxy): Promise<void> => {
      const host = canvasHostRef.current;
      if (!host || disposed) return;

      const currentVersion = ++renderVersion;
      const fragment = document.createDocumentFragment();
      const availableWidth = Math.max(260, host.clientWidth - (window.innerWidth >= 640 ? 32 : 16));
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        if (disposed || currentVersion !== renderVersion) return;
        const page = await pdf.getPage(pageNumber);
        const baseViewport = page.getViewport({ scale: 1 });
        const cssScale = Math.min(availableWidth / baseViewport.width, 2);
        const viewport = page.getViewport({ scale: cssScale * pixelRatio });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width / pixelRatio)}px`;
        canvas.style.height = `${Math.floor(viewport.height / pixelRatio)}px`;
        canvas.className = 'block max-w-full bg-white pointer-events-none';
        canvas.setAttribute('aria-label', `صفحه ${pageNumber} از ${pdf.numPages}`);

        const pageWrapper = document.createElement('div');
        pageWrapper.className = 'relative mx-auto w-fit max-w-full overflow-hidden rounded-lg bg-white shadow-xl';
        pageWrapper.append(canvas);

        const interactionGuard = document.createElement('div');
        interactionGuard.className = 'absolute inset-0 z-10 cursor-default select-none';
        interactionGuard.setAttribute('aria-hidden', 'true');
        interactionGuard.addEventListener('contextmenu', (event) => event.preventDefault());
        interactionGuard.addEventListener('dragstart', (event) => event.preventDefault());
        pageWrapper.append(interactionGuard);
        fragment.append(pageWrapper);

        await page.render({ canvas, viewport, annotationMode: 0 }).promise;
      }

      if (disposed || currentVersion !== renderVersion || !canvasHostRef.current) return;
      canvasHostRef.current.replaceChildren(fragment);
      setStatus('ready');
    };

    const loadPdf = async (): Promise<void> => {
      setStatus('loading');
      setPageCount(0);
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      loadingTask = pdfjs.getDocument({ url: pdfResourceUrl(url) });
      pdfDocument = await loadingTask.promise;
      if (disposed) return;
      setPageCount(pdfDocument.numPages);
      lastWidth = Math.round(canvasHostRef.current?.clientWidth ?? 0);
      if (canvasHostRef.current) observer.observe(canvasHostRef.current);
      await renderPages(pdfDocument);
    };

    void loadPdf().catch(() => {
      if (!disposed) setStatus('error');
    });

    return () => {
      disposed = true;
      renderVersion += 1;
      observer.disconnect();
      window.clearTimeout(resizeTimer);
      void loadingTask?.destroy();
    };
  }, [url]);

  return <div className="fixed inset-0 z-[70] flex flex-col bg-slate-950/95 p-2 sm:p-5" dir="rtl" onContextMenu={(event) => event.preventDefault()}>
    <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 rounded-t-2xl bg-slate-950 px-4 py-3 text-white sm:px-5">
      <div className="min-w-0"><p className="truncate text-sm font-black">{title}</p><p className="mt-1 text-[11px] text-slate-400">نمایش محافظت‌شده PDF{pageCount ? ` · ${new Intl.NumberFormat('fa-IR').format(pageCount)} صفحه` : ''}</p></div>
      <button aria-label="بستن نمایشگر PDF" className="grid size-10 shrink-0 place-items-center rounded-xl bg-white/10 transition hover:bg-white/20" onClick={onClose} type="button"><AppIcon name="close" size={19} /></button>
    </div>
    <div className="relative mx-auto min-h-0 w-full max-w-6xl flex-1 overflow-hidden rounded-b-2xl bg-slate-800">
      {status === 'loading' ? <div className="absolute inset-0 z-20 grid place-items-center bg-slate-900 text-sm font-bold text-slate-300">در حال آماده‌سازی PDF…</div> : null}
      {status === 'error' ? <div className="absolute inset-0 z-20 grid place-items-center bg-slate-900 p-6 text-center"><div><p className="text-sm font-black text-rose-300">نمایش PDF ممکن نشد</p><p className="mt-2 text-xs leading-6 text-slate-400">فایل در دسترس نیست یا سرور اجازه نمایش آن را نمی‌دهد.</p></div></div> : null}
      <div className="h-full space-y-3 overflow-y-auto overscroll-contain p-2 select-none sm:p-4" onCopy={(event) => event.preventDefault()} onDragStart={(event) => event.preventDefault()} ref={canvasHostRef} />
    </div>
  </div>;
};
